import { forwardRef, useId } from "react";
import { cn } from "../../lib/cn";

const baseInputClass =
  "form-input obsidian-input w-full h-11 px-3.5 rounded-lg text-ink text-sm placeholder:text-ink-subtle disabled:opacity-60";

export const Input = forwardRef(function Input(
  { label, hint, error, type = "text", className, id: providedId, leftIcon, rightIcon, ...rest },
  ref
) {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={id} className="block text-xs font-medium text-ink-muted">
          {label}
        </label>
      )}
      <div className="relative">
        {leftIcon && (
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-ink-subtle pointer-events-none">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={id}
          type={type}
          className={cn(
            baseInputClass,
            leftIcon && "pl-10",
            rightIcon && "pr-10",
            error && "border-danger focus:border-danger focus:ring-danger/30",
            className
          )}
          {...rest}
        />
        {rightIcon && (
          <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-[18px] text-ink-subtle pointer-events-none">
            {rightIcon}
          </span>
        )}
      </div>
      {hint && !error && <p className="text-xs text-ink-subtle">{hint}</p>}
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
});

export const Textarea = forwardRef(function Textarea(
  { label, hint, error, className, id: providedId, rows = 4, ...rest },
  ref
) {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={id} className="block text-xs font-medium text-ink-muted">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        id={id}
        rows={rows}
        className={cn(
          "form-textarea obsidian-input w-full px-3.5 py-2.5 rounded-lg text-ink text-sm placeholder:text-ink-subtle disabled:opacity-60 resize-y",
          error && "border-danger focus:border-danger focus:ring-danger/30",
          className
        )}
        {...rest}
      />
      {hint && !error && <p className="text-xs text-ink-subtle">{hint}</p>}
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
});

export const Select = forwardRef(function Select(
  { label, hint, error, className, id: providedId, options = [], children, ...rest },
  ref
) {
  const generatedId = useId();
  const id = providedId || generatedId;

  return (
    <div className="space-y-1.5">
      {label && (
        <label htmlFor={id} className="block text-xs font-medium text-ink-muted">
          {label}
        </label>
      )}
      <select
        ref={ref}
        id={id}
        className={cn(
          "form-select obsidian-input w-full h-11 px-3.5 pr-9 rounded-lg text-ink text-sm disabled:opacity-60",
          error && "border-danger focus:border-danger focus:ring-danger/30",
          className
        )}
        {...rest}
      >
        {children ||
          options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
      </select>
      {hint && !error && <p className="text-xs text-ink-subtle">{hint}</p>}
      {error && <p className="text-xs text-danger">{error}</p>}
    </div>
  );
});
